import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuthStore } from '@/store/authStore';
import { authApi } from '@/services/api';
import LoginPage from '@/pages/LoginPage';
import UnlockPage from '@/pages/UnlockPage';
import SignupPage from '@/pages/SignupPage';
import ResetPasswordPage from '@/pages/ResetPasswordPage';
import DashboardPage from '@/pages/DashboardPage';
import POSPage from '@/pages/POSPage';
import ItemsPage from '@/pages/ItemsPage';
import CustomersPage from '@/pages/CustomersPage';
import SuppliersPage from '@/pages/SuppliersPage';
import UsersPage from '@/pages/UsersPage';
import ExpensesPage from '@/pages/ExpensesPage';
import RawDataPage from '@/pages/RawDataPage';
import ReportsPage from '@/pages/ReportsPage';
import AnalyticsPage from '@/pages/AnalyticsPage';
import ActivityLogPage from '@/pages/ActivityLogPage';
import CustomerDisplayPage from '@/pages/CustomerDisplayPage';

import SettingsPage from '@/pages/SettingsPage';
import CommissionPage from '@/pages/CommissionPage';
import PromotionsPage from '@/pages/PromotionsPage';
import AccountPage from '@/pages/AccountPage';
import SupportPage from '@/pages/SupportPage';
import PriceCheckerPage from '@/pages/PriceCheckerPage';
import DailySalesEmailTestPage from '@/pages/DailySalesEmailTestPage';
import AdminRoute from '@/components/common/AdminRoute';
import PosAccessGuard from '@/components/common/PosAccessGuard';
import DesktopAuthGate from '@/components/desktop/DesktopAuthGate';
import CloudSessionBanner from '@/components/desktop/CloudSessionBanner';
import EnrollPinModal from '@/components/desktop/EnrollPinModal';

import { isCompanyAdmin, isManager, ROLES } from '@/types';
import { useOfflineInit } from '@/offline/useOfflineInit';
import CatalogSyncModal from '@/components/offline/CatalogSyncModal';
import { isDesktopApp } from '@/desktop/electronApi';
import { useDesktopSessionStore } from '@/desktop/desktopSessionStore';
import DesktopSetupWizard from '@/pages/DesktopSetupWizard';

function HomeRedirect() {
  const { isAuthenticated, role } = useAuthStore();
  const setupComplete = useDesktopSessionStore((s) => s.setupComplete);
  if (!isAuthenticated) {
    if (isDesktopApp() && setupComplete) return <Navigate to="/unlock" replace />;
    return <Navigate to="/login" replace />;
  }
  if (isDesktopApp() && !setupComplete) return <Navigate to="/desktop-setup" replace />;
  return <Navigate to={isCompanyAdmin(role) || isManager(role) ? '/dashboard' : '/'} replace />;
}

function App() {
  const { isAuthenticated, clearAuth, syncSession } = useAuthStore();
  const [sessionChecked, setSessionChecked] = useState(false);
  const pendingEnroll = useDesktopSessionStore((s) => s.pendingEnrollAfterLogin);
  const setPendingEnroll = useDesktopSessionStore((s) => s.setPendingEnrollAfterLogin);
  const resetPinAfterLogin = useDesktopSessionStore((s) => s.resetPinAfterLogin);
  const setCloudExpired = useDesktopSessionStore((s) => s.setCloudSessionExpired);
  const locallyUnlocked = useDesktopSessionStore((s) => s.locallyUnlocked);
  const setupComplete = useDesktopSessionStore((s) => s.setupComplete);
  useOfflineInit();

  useEffect(() => {
    // Desktop cold start: require unlock before probing cloud (no active sid yet).
    if (isDesktopApp() && !locallyUnlocked) {
      setSessionChecked(true);
      return;
    }
    if (isAuthenticated) {
      authApi.getCurrentUser()
        .then((res) => {
          const data = res.data.message;
          if (data?.user) {
            syncSession({
              user: data.user,
              company: data.company?.code
                ? { code: data.company.code, name: data.company.name || '', status: data.company.status || '' }
                : null,
              role: data.role || '',
              can_use_pos: Boolean(data.can_use_pos),
              permissions: data.permissions,
            });
            void import('@/i18n').then(({ setAppLanguage }) => {
              void setAppLanguage(data.user?.ui_language, data.company_default_ui_language);
            });
            setCloudExpired(false);
          }
        })
        .catch(() => {
          if (isDesktopApp()) {
            setCloudExpired(true);
          } else {
            clearAuth();
          }
        })
        .finally(() => setSessionChecked(true));
    } else {
      setSessionChecked(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locallyUnlocked]);

  // Apply company currency/language early so POS product grids match cart totals
  useEffect(() => {
    if (!isAuthenticated) return;
    void authApi.getCompanyProfile().then((res) => {
      const profile = res.data.message;
      if (!profile) return;
      void import('@/utils/currency').then(({ setLocaleContext }) => {
        setLocaleContext({
          country: profile.country || 'PK',
          currency: profile.currency || 'PKR',
          uiLanguage: profile.ui_language || 'en',
        });
      });
      void import('@/i18n').then(({ setAppLanguage }) => {
        const userLang = useAuthStore.getState().user?.ui_language;
        void setAppLanguage(userLang, profile.ui_language || 'en');
      });
    }).catch(() => {});
  }, [isAuthenticated]);

  if (!sessionChecked) return null;

  const loginRedirect = isDesktopApp()
    ? setupComplete
      ? '/unlock'
      : '/login'
    : '/login';

  return (
    <DesktopAuthGate>
      <CatalogSyncModal />
      <CloudSessionBanner />
      {pendingEnroll && isDesktopApp() && isAuthenticated && setupComplete && (
        <EnrollPinModal
          open
          required={resetPinAfterLogin}
          title={resetPinAfterLogin ? 'Reset unlock PIN' : 'Set PIN for offline desktop unlock'}
          onClose={() => setPendingEnroll(false)}
          onEnrolled={() => setPendingEnroll(false)}
        />
      )}
      <Routes>
      <Route
        path="/desktop-setup"
        element={
          isDesktopApp() && isAuthenticated && !setupComplete ? (
            <DesktopSetupWizard />
          ) : (
            <HomeRedirect />
          )
        }
      />
      <Route path="/unlock" element={<UnlockPage />} />
      <Route
        path="/login"
        element={
          !isAuthenticated ? (
            <LoginPage />
          ) : isDesktopApp() && !setupComplete ? (
            <Navigate to="/desktop-setup" replace />
          ) : (
            <HomeRedirect />
          )
        }
      />
      <Route path="/signup" element={!isAuthenticated ? <SignupPage /> : <HomeRedirect />} />
      <Route path="/reset-password" element={!isAuthenticated ? <ResetPasswordPage /> : <HomeRedirect />} />
      <Route path="/dashboard" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><DashboardPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/" element={isAuthenticated ? <PosAccessGuard><POSPage /></PosAccessGuard> : <Navigate to={loginRedirect} />} />
      <Route path="/price-checker" element={isAuthenticated ? <PosAccessGuard><PriceCheckerPage /></PosAccessGuard> : <Navigate to={loginRedirect} />} />
      <Route path="/items" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ItemsPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/customers" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><CustomersPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/suppliers" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><SuppliersPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/expenses" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ExpensesPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/users" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><UsersPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/commission" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><CommissionPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/promotions" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><PromotionsPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/raw-data" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN]}><RawDataPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/analytics" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><AnalyticsPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports" element={isAuthenticated ? <Navigate to="/reports/sales/register" replace /> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/daily-cash-ledger" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="general" report="daily-cash-ledger" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales" element={isAuthenticated ? <Navigate to="/reports/sales/register" replace /> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/purchases" element={isAuthenticated ? <Navigate to="/reports/purchases/register" replace /> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/register" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="register" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/summary" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="summary" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/party-summary" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="party-summary" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/recovery" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="recovery" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/activity-invoice" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="activity-invoice" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/activity-party" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="activity-party" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/items-summary" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="items-summary" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/items-analysis" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="items-analysis" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/services-analysis" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="services-analysis" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/x-report" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.CASHIER]}><ReportsPage section="sales" report="x-report" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/z-report" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER, ROLES.CASHIER]}><ReportsPage section="sales" report="z-report" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/z-day" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="z-day" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/shift-performance" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="shift-performance" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/commission" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="commission" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/sales/promotions" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="sales" report="promotions" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/purchases/register" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="purchases" report="register" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/purchases/summary" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="purchases" report="summary" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/purchases/supplier-summary" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="purchases" report="supplier-summary" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/purchases/activity-invoice" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="purchases" report="activity-invoice" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/purchases/activity-supplier" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="purchases" report="activity-supplier" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/purchases/item-analysis" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="purchases" report="item-analysis" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/purchases/payments" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="purchases" report="payments" /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/reports/purchases/outstanding" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ReportsPage section="purchases" report="outstanding" /></AdminRoute> : <Navigate to={loginRedirect} />} />

      <Route path="/support" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN]}><SupportPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/customer-display" element={<CustomerDisplayPage />} />
      <Route path="/activity-log" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN, ROLES.MANAGER]}><ActivityLogPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/settings" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN]}><SettingsPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
      <Route path="/account" element={isAuthenticated ? <AccountPage /> : <Navigate to={loginRedirect} />} />
      <Route path="/printer" element={isAuthenticated ? <Navigate to="/settings?tab=printer" replace /> : <Navigate to={loginRedirect} />} />
      <Route path="/test-daily-sale" element={isAuthenticated ? <AdminRoute allowedRoles={[ROLES.COMPANY_ADMIN]}><DailySalesEmailTestPage /></AdminRoute> : <Navigate to={loginRedirect} />} />
    </Routes>
    </DesktopAuthGate>
  );
}
export default App;
